import { useEffect, useState } from 'react'
import { Save, RotateCcw, Eye, EyeOff, CheckCircle2, XCircle, Info } from 'lucide-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Label,
  Checkbox,
  Select,
  Badge,
} from '@/components/ui'
import { settingsStorage } from '@/lib/settings'
import { isEnvConfigured } from '@/lib/env'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import type { Settings } from '@/types'

const MODEL_OPTIONS = [
  { value: 'gpt-4o', label: 'GPT-4o (Recommended)' },
  { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Faster)' },
  { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
]

const BROWSER_OPTIONS = [
  { value: 'chromium', label: 'Chrome (Chromium)' },
  { value: 'firefox', label: 'Firefox' },
  { value: 'webkit', label: 'WebKit (Safari)' },
]

const THEME_OPTIONS = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
]

// Renamed from Settings → Preferences in Phase 5c-1 — these are
// device/user preferences (theme, API key, browser config) stored in
// localStorage, NOT workspace settings. The `/workspaces/:wid/settings`
// route now hosts the new tabbed WorkspaceSettings UI.
export default function Preferences() {
  const [settings, setSettings] = useState<Settings | null>(null)
  const [showApiKey, setShowApiKey] = useState(false)
  const [apiKeyStatus, setApiKeyStatus] = useState<'idle' | 'testing' | 'valid' | 'invalid'>('idle')
  const [hasChanges, setHasChanges] = useState(false)
  // Reset-confirmation dialog — replaces native window.confirm() so
  // the modal matches the rest of the app's themed dialogs. Non-
  // destructive action (reset is recoverable by re-entering values
  // or the next save), so `confirmVariant` flips off `destructive`.
  const [confirmResetOpen, setConfirmResetOpen] = useState(false)

  // Check if API key is from environment
  const isApiKeyFromEnv = isEnvConfigured('VITE_OPENAI_API_KEY')

  useEffect(() => {
    loadSettings()
  }, [])

  function loadSettings() {
    const data = settingsStorage.get()
    setSettings(data)
  }

  function updateSettings(updates: Partial<Settings>) {
    if (!settings) return
    setSettings({ ...settings, ...updates })
    setHasChanges(true)
  }

  function updateOpenAI(updates: Partial<Settings['openai']>) {
    if (!settings) return
    setSettings({
      ...settings,
      openai: { ...settings.openai, ...updates },
    })
    setHasChanges(true)
  }

  function updateBrowser(updates: Partial<Settings['browser']>) {
    if (!settings) return
    setSettings({
      ...settings,
      browser: { ...settings.browser, ...updates },
    })
    setHasChanges(true)
  }

  function saveSettings() {
    if (!settings) return
    settingsStorage.save(settings)
    setHasChanges(false)
  }

  function resetSettings() {
    // Just opens the themed confirm dialog; the actual reset happens
    // in the dialog's onConfirm so the busy-state + error handling
    // live there rather than scattered through this function.
    setConfirmResetOpen(true)
  }

  async function testApiKey() {
    if (!settings?.openai.apiKey) return

    setApiKeyStatus('testing')

    try {
      // In real implementation, this would call the OpenAI API
      await new Promise((resolve) => setTimeout(resolve, 1000))

      // Simulate validation (check if key starts with 'sk-')
      if (settings.openai.apiKey.startsWith('sk-')) {
        setApiKeyStatus('valid')
      } else {
        setApiKeyStatus('invalid')
      }
    } catch {
      setApiKeyStatus('invalid')
    }
  }

  if (!settings) {
    return <div>Loading...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Settings</h1>
          <p className="text-muted-foreground">
            Configure API keys, browser settings, and preferences
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={resetSettings}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button onClick={saveSettings} disabled={!hasChanges}>
            <Save className="h-4 w-4 mr-2" />
            Save
          </Button>
        </div>
      </div>

      {/* OpenAI Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>OpenAI Configuration</CardTitle>
          <CardDescription>
            Configure your OpenAI API key and model preferences
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label>API Key</Label>
              {isApiKeyFromEnv && (
                <Badge variant="secondary" className="text-xs">
                  From .env
                </Badge>
              )}
            </div>
            {isApiKeyFromEnv && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-muted text-sm">
                <Info className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                <div>
                  <p className="text-muted-foreground">
                    API key is configured via <code className="text-xs bg-background px-1 py-0.5 rounded">VITE_OPENAI_API_KEY</code> environment variable.
                  </p>
                  <p className="text-muted-foreground mt-1">
                    To change it, update your <code className="text-xs bg-background px-1 py-0.5 rounded">.env</code> file and restart the dev server.
                  </p>
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  type={showApiKey ? 'text' : 'password'}
                  value={settings.openai.apiKey ?? ''}
                  onChange={(e) => {
                    updateOpenAI({ apiKey: e.target.value || undefined })
                    setApiKeyStatus('idle')
                  }}
                  placeholder="sk-..."
                  disabled={isApiKeyFromEnv}
                  className={isApiKeyFromEnv ? 'bg-muted' : ''}
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <Button
                variant="outline"
                onClick={testApiKey}
                disabled={!settings.openai.apiKey || apiKeyStatus === 'testing'}
              >
                {apiKeyStatus === 'testing' ? 'Testing...' : 'Test'}
              </Button>
            </div>
            {apiKeyStatus === 'valid' && (
              <p className="text-sm text-success flex items-center gap-1">
                <CheckCircle2 className="h-4 w-4" />
                API key is valid
              </p>
            )}
            {apiKeyStatus === 'invalid' && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <XCircle className="h-4 w-4" />
                Invalid API key
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Model</Label>
              <Select
                options={MODEL_OPTIONS}
                value={settings.openai.model}
                onChange={(e) => updateOpenAI({ model: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Temperature ({settings.openai.temperature})</Label>
              <Input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={settings.openai.temperature}
                onChange={(e) => updateOpenAI({ temperature: parseFloat(e.target.value) })}
              />
              <p className="text-xs text-muted-foreground">
                Lower = more deterministic, Higher = more creative
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Browser Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>Browser Configuration</CardTitle>
          <CardDescription>
            Configure Playwright browser settings for test execution
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Browser</Label>
              <Select
                options={BROWSER_OPTIONS}
                value={settings.browser.type}
                onChange={(e) =>
                  updateBrowser({ type: e.target.value as 'chromium' | 'firefox' | 'webkit' })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Execution Mode</Label>
              <div className="flex gap-4 pt-2">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={settings.browser.headless}
                    onChange={() => updateBrowser({ headless: true })}
                    className="accent-primary"
                  />
                  <span className="text-sm">Headless</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={!settings.browser.headless}
                    onChange={() => updateBrowser({ headless: false })}
                    className="accent-primary"
                  />
                  <span className="text-sm">Headed</span>
                </label>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Viewport Width</Label>
              <Input
                type="number"
                placeholder="1920"
                value={settings.browser.viewportWidth ?? ''}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10)
                  updateBrowser({ viewportWidth: isNaN(n) ? undefined : n })
                }}
              />
            </div>
            <div className="space-y-2">
              <Label>Viewport Height</Label>
              <Input
                type="number"
                placeholder="1080"
                value={settings.browser.viewportHeight ?? ''}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10)
                  updateBrowser({ viewportHeight: isNaN(n) ? undefined : n })
                }}
              />
            </div>
            <div className="space-y-2">
              <Label>Timeout (ms)</Label>
              <Input
                type="number"
                value={settings.browser.timeout}
                onChange={(e) => updateBrowser({ timeout: parseInt(e.target.value) || 30000 })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Options</Label>
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={settings.browser.screenshotOnFailure}
                  onCheckedChange={(checked) => updateBrowser({ screenshotOnFailure: checked })}
                />
                <span className="text-sm">Capture screenshots on failure</span>
              </label>
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={settings.browser.recordVideo}
                  onCheckedChange={(checked) => updateBrowser({ recordVideo: checked })}
                />
                <span className="text-sm">Record video of test runs</span>
              </label>
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={settings.browser.slowMotion}
                  onCheckedChange={(checked) => updateBrowser({ slowMotion: checked })}
                />
                <span className="text-sm">Slow motion mode (500ms delay between actions)</span>
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Appearance */}
      <Card>
        <CardHeader>
          <CardTitle>Appearance</CardTitle>
          <CardDescription>
            Customize the look and feel
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label>Theme</Label>
            <Select
              options={THEME_OPTIONS}
              value={settings.theme}
              onChange={(e) => updateSettings({ theme: e.target.value as 'light' | 'dark' | 'system' })}
              className="w-48"
            />
          </div>
        </CardContent>
      </Card>

      {/* Data Storage */}
      <Card>
        <CardHeader>
          <CardTitle>Data Storage</CardTitle>
          <CardDescription>
            Manage locally stored data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Application data is stored in MongoDB. Settings are stored locally in your browser.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm">
              Export All Data
            </Button>
            <Button variant="outline" size="sm">
              Import Data
            </Button>
            <Button variant="destructive" size="sm">
              Clear All Results
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Reset-to-defaults confirmation. `default` variant (not
          destructive) since reset is recoverable — re-entering values
          or the next save restores anything important. Replaces the
          previous native window.confirm(). */}
      <ConfirmDialog
        open={confirmResetOpen}
        onClose={() => setConfirmResetOpen(false)}
        title="Reset all settings to defaults?"
        description="Your current settings will be cleared and replaced with the application defaults."
        confirmLabel="Reset"
        confirmVariant="default"
        onConfirm={() => {
          settingsStorage.reset()
          loadSettings()
          setHasChanges(false)
          setConfirmResetOpen(false)
        }}
      />
    </div>
  )
}
